import React from 'react';
import { Box, Skeleton, Stack, Table, TableHead, TableRow, TableCell, TableBody, useMediaQuery, useTheme } from '@mui/material';
import PageContainer from '../components/common/PageContainer';
import Panel from '../components/common/Panel';
import KpiRow, { KpiCell } from '../components/common/KpiRow';
import { SP, BUTTON_RADIUS, FIELD_HEIGHT_SX } from '../theme/tokens';

/**
 * VendorsOverviewSkeleton — Ladezustand der Lieferantenliste.
 *
 * 🔴 Er bildet die GELADENE Flaeche nach, nicht nur ihren Umriss (R16-B-W1,
 * 2026-10-04): Kopfzeile mit EINER Aktion (die Flaeche traegt genau einen Knopf
 * „Add vendor"), der KPI-Streifen mit FUENF Kacheln, ein Filterstreifen mit
 * EINER Suche und die Tabelle mit SECHS Spalten. Die KPI-Reihe ist der teure
 * Teil: fehlte sie, schoebe das Eintreffen der Daten den ganzen Panel-Block nach
 * unten — genau das „Springen", das ein Skelett verhindern soll.
 *
 * Der Mobil-Zweig zeichnet die Kartenliste, nicht die Tabelle — sonst erschiene
 * auf einem Telefon erst eine Tabelle, dann eine Kartenliste.
 *
 * `role="status"` + `aria-label` + `aria-busy` melden den Ladezustand
 * Hilfsmitteln an, wie es `VendorAnschriften.jsx` tut (R16-A-W3).
 *
 * `anti-patterns.md` §11 verlangt Skeletons beim Seitenaufbau. Werte aus
 * `theme/tokens` — Rule 22 laesst neue Literale nicht zu.
 */
export default function VendorsOverviewSkeleton() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    // 🔴 G9a (§2.2 Punkt 1): `full` wie die geladene Seite — sonst springt der
    // Rahmen beim Eintreffen der Daten von `reading` auf `full`.
    <PageContainer width="full">
      {/* 🔴 G9a (§2.2 Punkt 2): die Messmarke des Ladezustands. Sie steht NICHT
          in `ZONEN` und geht in keine Zonen-Reihenfolge ein — `oeffne()` wartet
          auf ihr Verschwinden (`selektoren.js`, `skelett`). */}
      <Box role="status" aria-label="Loading vendors" aria-busy="true" data-testid="liste-skelett">
        {/* 🔴 R16 C1 (2026-10-06): KEIN Kopf-Platzhalter mehr. Den Kopf rendert die
            Seite in JEDEM Zustand (`VendorsOverview.jsx`, `const kopf`) — der graue
            Balken hier war der Grund, warum im Laden GAR KEIN Titel stand (die mobile
            Leiste schweigt seit 5(i)). Das Skelett zeichnet nur den Inhalt. */}

        {/* KPI-Streifen: fuenf Kacheln — aus `KpiRow` selbst.
            🔴 R15 B2 (2026-10-08): hier stand die ALTE Kachelform
            (`flex: { xs: '1 1 calc(50% - 1px)', sm: '1 1 160px' }`) in einem
            eigenen `Box`. Mit `flex-wrap` wuchs die allein umbrechende Kachel
            auf die RESTBREITE der Zeile — genau der Fotobefund, den F8 in
            `KpiRow` behebt; im Geruest stand er unveraendert weiter, sichtbar
            als Sprung beim Umschalten von Laden auf Geladen. Geruest und
            geladene Reihe fahren jetzt DENSELBEN Baustein, wie es
            `CompetitorDetailSkeleton` und `ProductDetailSkeleton` seit Welle 4
            tun. Der Aussenabstand bleibt `SP.md`: geladen traegt die Reihe
            `mb: 3` (= 24 px = `SP.md`, `VendorsOverview.jsx:476`). */}
        <KpiRow sx={{ mb: SP.md }}>
          {[...Array(5)].map((_, i) => (
            <KpiCell key={i}>
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="text" width="40%" sx={{ height: 28 }} />
            </KpiCell>
          ))}
        </KpiRow>

        <Panel sx={{ mb: SP.md, maxWidth: '100%', overflow: 'hidden' }}>
          {/* Filterstreifen: EINE Suche + Zaehler — und mobil der Oeffner.
              🔴 R16-C3 (2026-10-21): hier stand eine Suche mit `width: { xs:
              '100%', sm: 320 }` und KEIN Oeffner. Die geladene Leiste traegt
              aber `flex: 1, minWidth: 0` (`VendorsOverview.jsx`, `search.sx`)
              — bei 1440 rund 980 px — und unter `sm` den „Filters"-Oeffner.
              Beim Eintreffen der Daten sprang die Suchflaeche damit von 320 px
              auf die Restbreite und der Knopf erschien; genau das soll ein
              Geruest verhindern (eigener Docstring oben). */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: SP.card, gap: SP.card }}>
            {/* 🔴 Die Form JE BREAKPOINT, nicht ein Radius fuer alle Breiten
                (R16 B2/W2, 2026-10-07): unter `sm` ist das echte Suchfeld eine
                PILLE — `SearchTrigger` zeichnet mobil eine `Box` mit
                `borderRadius: 99` (`components/common/SearchTrigger.jsx:437`),
                erst ab `sm` ein `TextField` mit dem Themenradius. Hausform fuer
                beides: `{ xs: '99px', sm: `${BUTTON_RADIUS}px` }`, Vorbild
                `POStatusBar.jsx:874`. Ein flacher Radius waere mobil dreimal zu
                eckig, ein nacktes `BUTTON_RADIUS` waere 36 px.
                🔴 Und die HOEHE kommt aus DEMSELBEN Token wie die der Pille
                (T3-5, 2026-10-08). Hier stand „und `height: 40`" — fest, auf
                jeder Breite —, waehrend `SearchTrigger` seit T3-5
                `FIELD_HEIGHT_SX` traegt: unter `sm` sind das 44. Das Geruest
                war auf dem Telefon 4 px zu kurz, und beim Eintreffen der Daten
                sprang die Zeile. Kein zweiter Wert — derselbe Token, dieselbe
                Hausform wie die Pille. */}
            <Skeleton
              data-testid="skelett-suche"
              variant="rectangular"
              sx={{
                ...FIELD_HEIGHT_SX,
                // Dieselbe Breitenregel wie die geladene Suche: mobil die
                // Pille (`flex: '1 1 auto'`, `SearchTrigger` geschlossen),
                // ab `sm` der Rest der Leiste (`flex: 1, minWidth: 0`).
                flex: { xs: '1 1 auto', sm: 1 },
                minWidth: 0,
                borderRadius: { xs: '99px', sm: `${BUTTON_RADIUS}px` },
              }}
            />
            {/* R16-C3: der „Filters"-Oeffner der geladenen Leiste — nur unter
                `sm` (`moreFilters` haengt an `isMobile`). */}
            <Skeleton
              data-testid="skelett-filters"
              variant="rounded"
              sx={{
                ...FIELD_HEIGHT_SX,
                display: { xs: 'block', sm: 'none' },
                width: 84,
                borderRadius: `${BUTTON_RADIUS}px`,
              }}
            />
            {/* Der Zaehler steht mobil in Z3, nicht in Z1 (`ListToolbar`). */}
            <Skeleton variant="text" width={90} sx={{ display: { xs: 'none', sm: 'block' }, ml: 'auto' }} />
          </Box>

          {isMobile ? (
            /* Mobil: Kartenliste */
            <Stack spacing={0}>
              {/* 🔴 R16-Runde-2 W1 (2026-10-21): Z3-Zaehler und Sortiersatz.
                  Geladen stehen sie mobil zwischen Leiste und Karten
                  (`ListToolbar`, `VendorsOverview.jsx`); fehlten sie hier,
                  schob sich der Stapel beim Eintreffen um rund 50 px nach
                  unten — derselbe Sprung, den R16-C3 oben beseitigt hat. */}
              <Box sx={{ px: SP.card, pt: SP.sm, mb: SP.tight }}>
                <Skeleton
                  data-testid="skelett-zaehler"
                  variant="text"
                  width={90}
                  sx={{ mb: SP.tight }}
                />
                <Skeleton data-testid="skelett-sortiersatz" variant="text" width={160} />
              </Box>
              {[...Array(6)].map((_, r) => (
                <Box key={r} sx={{ borderBottom: 1, borderColor: 'divider', px: SP.card, py: SP.sm }}>
                  <Skeleton variant="text" width="45%" />
                  <Skeleton variant="text" width="65%" />
                  <Skeleton variant="text" width="35%" />
                </Box>
              ))}
            </Stack>
          ) : (
            /* Desktop: Tabelle mit SECHS Spalten, wie `headCells` der Flaeche */
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[...Array(6)].map((_, i) => (
                    <TableCell key={i}><Skeleton variant="text" width={75} /></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(8)].map((_, r) => (
                  <TableRow key={r}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: SP.sm }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width="70%" />
                      </Box>
                    </TableCell>
                    {[...Array(5)].map((_, c) => (
                      <TableCell key={c}><Skeleton variant="text" width="65%" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
      </Box>
    </PageContainer>
  );
}
